import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { Column } from '@repo/ui/data-table'
import { LocationOneIcon, PlusIcon, WorldIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { getCountry } from '../../api/countries/get-country'
import { updateCountry } from '../../api/countries/update-country'
import { createJourneyPlace } from '../../api/journey-place/create-journey-place'
import { updateJourneyPlace } from '../../api/journey-place/update-journey-place'
import DateChip from '../../components/elements/date-chip'
import { DynamicTableRef } from '../../components/elements/dynamic-table'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import NestedDataTable from '../../components/elements/nested-table'
import CountryForm from '../../components/pages/countries/country-form'
import JourneyPlaceLightForm from '../../components/pages/journey-places/journey-place-light-form'
import { Paths } from '../../lib/constants/path'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { accomodationEnumMapping } from '../../lib/types/enums/accomodation-type/accomodation-type'
import {
  JourneyPlace,
  JourneyPlaceFilters
} from '../../lib/types/models/journey-place/journey-place'
import {
  modalButtonConfig,
  updateButtonsConfig
} from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'
import { mapCountryToInput } from './utils'

const UpdateCountryPage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const tableRef = React.useRef<DynamicTableRef<JourneyPlace>>(null)
  const { navigate, back } = useNavigator()

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    JourneyPlace
  >(['create', 'edit'])

  if (!id || isNaN(parseInt(id))) return <ItemNotFound title="Identifiant de pays invalide" />

  const { mutateAsync } = useMutation({
    mutationFn: updateCountry,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['country', id] })
      toast.success('Pays modifié avec succès')
    }
  })

  const { mutateAsync: createJourneyPlaceMutation } = useMutation({
    mutationFn: createJourneyPlace,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Lieu de séjour créé avec succès')
    },
    onError: showAxiosError
  })

  const { mutateAsync: updateJourneyPlaceMutation } = useMutation({
    mutationFn: updateJourneyPlace,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Lieu de séjour modifié avec succès')
    }
  })

  const { data: country, isLoading: loadingCountry } = useQuery({
    queryKey: ['country', id],
    queryFn: () => getCountry(parseInt(id))
  })

  const columns: Column<JourneyPlace>[] = [
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true
    },
    {
      accessorKey: 'accomodation',
      header: "Type d'hébergement",
      enableSorting: true,
      cell: ({ row }) => accomodationEnumMapping[row.original.accomodation]
    }
  ]

  if (loadingCountry) return <LoadingScreen />
  if (!country) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={country.name} icon={WorldIcon} />
      <div className={'h-4'} />
      <Box className={'flex flex-col gap-4'}>
        <div className={'flex items-center gap-3'}>
          <DateChip date={country.createdAt} label={'Créé'} />
          <DateChip date={country.updatedAt} label={'Mise à jour'} />
        </div>

        <CountryForm
          defaultValues={mapCountryToInput(country)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.COUNTRY_LIST_PAGE),
            onUpdate: (values) => mutateAsync({ id: country.id, ...values }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: country.id, ...values })
              navigate(Paths.COUNTRY_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: country.id, ...values })
              navigate(Paths.COUNTRY_LIST_PAGE, { replace: true })
            }
          })}
        />
      </Box>

      <div className={'h-4'} />

      <NestedDataTable<JourneyPlace, JourneyPlaceFilters>
        ref={tableRef}
        title={'Lieux de séjour'}
        deleteMessage={{
          name: 'lieux de séjour',
          attributeName: 'name',
          deleteOne: (journeyPlace) =>
            `Êtes-vous sûr de vouloir supprimer ce lieu de séjour "${journeyPlace.name}"?`
        }}
        onDelete={() => toast.success('Lieu de séjour supprimé avec succès')}
        columns={columns}
        isSearchable
        filters={{ countryIds: [country.id] }}
        onEditAction={(journeyPlace) => toggleModal('edit', journeyPlace)}
        endpoint={API.journeyPlaces.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Créer un nouveau lieu de séjour',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <Modal
        isOpen={modalState.create}
        title={'Lieu de séjour'}
        icon={LocationOneIcon}
        hideActions
        onCancel={() => closeModel('create')}
      >
        <JourneyPlaceLightForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{ countryId: country.id }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: (values) => createJourneyPlaceMutation(values)
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Lieu de séjour'}
          icon={LocationOneIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <JourneyPlaceLightForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              name: selectedData.name,
              accomodation: selectedData.accomodation,
              countryId: selectedData.countryId,
              address: selectedData.address
            }}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: (values) => updateJourneyPlaceMutation({ id: selectedData.id, ...values })
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default UpdateCountryPage
